import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaPlay, FaStop, FaSpinner } from "react-icons/fa";
import { fetchOrgVoicePreview } from "../../api";

/**
 * Two dropdowns (provider, then voice) and a list of the provider's voices,
 * each with a description and a play button that fetches the preview clip
 * through the backend and plays it in the browser.
 */
export default function VoicePicker({ voices, providers, value, onChange, env, previewSentence }) {
  const selected = useMemo(() => voices.find((voice) => voice.key === value), [voices, value]);
  const [provider, setProvider] = useState(selected?.provider || providers[0]?.id || "azure");
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const urlsRef = useRef({});

  useEffect(() => {
    if (selected?.provider && selected.provider !== provider) {
      setProvider(selected.provider);
    }
    // Follow the stored voice's provider when the form opens; a later manual
    // provider change must not be undone by this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.key]);

  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const providerVoices = voices.filter((voice) => voice.provider === provider);

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(null);
  };

  const play = async (voice) => {
    if (playing === voice.key) {
      stop();
      return;
    }
    stop();
    setError(null);
    try {
      if (!urlsRef.current[voice.key]) {
        setLoading(voice.key);
        const blob = await fetchOrgVoicePreview(voice.provider, voice.id, env);
        urlsRef.current[voice.key] = URL.createObjectURL(blob);
      }
      const audio = new Audio(urlsRef.current[voice.key]);
      audioRef.current = audio;
      audio.onended = () => setPlaying(null);
      setPlaying(voice.key);
      await audio.play();
    } catch (err) {
      setPlaying(null);
      setError(previewErrorText(err, voice));
    } finally {
      setLoading(null);
    }
  };

  const choose = (nextProvider) => {
    setProvider(nextProvider);
    const first = voices.find((voice) => voice.provider === nextProvider);
    if (first && selected?.provider !== nextProvider) {
      onChange(first.key);
    }
  };

  return (
    <div className="oo-voice-picker">
      <div className="oo-field-grid">
        <div className="oo-field">
          <label>Voice provider</label>
          <select value={provider} onChange={(e) => choose(e.target.value)}>
            {providers.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="oo-field">
          <label>Voice</label>
          <select value={value} onChange={(e) => onChange(e.target.value)}>
            {!selected ? <option value={value}>{value}</option> : null}
            {providerVoices.map((voice) => (
              <option key={voice.key} value={voice.key}>
                {voice.name} — {voice.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="oo-voice-list" role="list">
        {providerVoices.map((voice) => {
          const isSelected = voice.key === value;
          const isPlaying = playing === voice.key;
          const isLoading = loading === voice.key;
          return (
            <div
              key={voice.key}
              role="listitem"
              className={`oo-voice-row ${isSelected ? "oo-voice-row--selected" : ""}`}
            >
              <button
                type="button"
                className="oo-voice-play"
                aria-label={isPlaying ? `Stop ${voice.name}` : `Play ${voice.name}`}
                onClick={() => play(voice)}
                disabled={isLoading}
              >
                {isLoading ? <FaSpinner className="oo-spin" /> : isPlaying ? <FaStop /> : <FaPlay />}
              </button>
              <button
                type="button"
                className="oo-voice-pick"
                onClick={() => onChange(voice.key)}
                aria-pressed={isSelected}
              >
                <span className="oo-voice-name">{voice.name}</span>
                <span className="oo-voice-desc">{voice.description}</span>
              </button>
              {isSelected ? <span className="oo-chip oo-chip--ok">Selected</span> : null}
            </div>
          );
        })}
      </div>
      {previewSentence ? (
        <div className="oo-field-help">Each preview says: “{previewSentence}”</div>
      ) : null}
      {error ? <div className="oo-form-errors"><div>{error}</div></div> : null}
    </div>
  );
}

function previewErrorText(err, voice) {
  const message = String(err?.message || "");
  if (/→ 503/.test(message)) {
    return `No preview for ${voice.name} yet: that provider's key is not configured on the voice service.`;
  }
  if (/→ 404/.test(message)) {
    return `The voice service does not know ${voice.name}.`;
  }

  return `Could not load the preview for ${voice.name}.`;
}
